import defaultTheme from './default'
import successTheme from './success'
import secondaryTheme from './secondary'
import darkTheme from './dark'

import { createMuiTheme } from '@material-ui/core'

const overrides = {
    typography: {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        fontSize: 12,
        h1: {
            fontSize: '1.8rem',
            color: "#151614",
            fontWeight: 700
        },
        h2: {
            fontSize: '1.4rem',
            fontWeight: 700
        },
        h3: {
            fontSize: '1.2rem',
            fontWeight: 700
        },
        h4: {
            fontSize: '1.1rem',
            fontWeight: 700
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 600
        },
        h6: {
            fontSize: '0.9rem',
            fontWeight: 600
        },
    },
    shape: { borderRadius: 8 },
    // Distinct chart-series colours matching the customer dashboard palette, so
    // overlapping lines/areas stay readable (primary & secondary are both brand red).
    series: {
        stop: '#E32C0F',     // red
        vov: '#2563EB',      // blue (dashboard VOV)
        panic: '#2B7A5B',    // green
        warning: '#F7B500',  // amber
        disable: '#7C3AED',  // purple
        people: '#E32C0F',   // red
        vehicle: '#2563EB',  // blue (dashboard VDU)
    },
    props: {
        MuiButton: { disableElevation: true },
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                // Bare <a> links (e.g. device codes, Zendesk tickets) are not
                // theme-aware and fell back to browser blue. Bring them on-brand:
                // ink + semibold, red on hover as the interaction cue.
                a: {
                    color: '#E32C0F',
                    fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'color 120ms ease',
                    '&:hover': {
                        color: '#B91F06',
                        textDecoration: 'underline',
                    },
                },
            },
        },
        MuiAppBar: {
            // Dark "admin chrome" so the portal reads clearly different from the
            // customer dashboard's white header. Header content is already light
            // (white logotype + translucent-white icons), so it stays legible.
            colorPrimary: {
                backgroundColor: '#151614',
                color: '#FFFFFF',
            },
        },
        MuiButton: {
            root: {
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
            },
            containedPrimary: {
                '&:hover': { backgroundColor: '#B91F06' },
            },
            outlinedPrimary: {
                borderColor: '#E32C0F',
                color: '#E32C0F',
                '&:hover': { borderColor: '#B91F06', backgroundColor: '#FDECEA' },
            },
        },
        MuiCard: {
            root: {
                borderRadius: 10,
                border: '1px solid #E8E7DF',
                boxShadow:
                    '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
            },
        },
        MUIDataTable: {
            paper: {
                boxShadow:
                    '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
            },
        },
        MuiBackdrop: {
            root: {
                backgroundColor: '#4A4A4A1A',
            },
        },
        MuiMenu: {
            paper: {
                boxShadow:
                    '0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A',
            },
        },
        MuiSelect: {
            icon: {
                color: '#B9B9B9',
            },
        },
        MuiListItem: {
            root: {
                '&$selected': {
                    backgroundColor: '#F3F5FF !important',
                    '&:focus': {
                        backgroundColor: '#F3F5FF',
                    },
                },
            },
            button: {
                '&:hover, &:focus': {
                    backgroundColor: '#F3F5FF',
                },
            },
        },
        MuiTouchRipple: {
            child: {
                backgroundColor: 'white',
            },
        },
        MuiTableRow: {
            root: {
                height: 32,
            },
        },
        MuiTableCell: {
            root: {
                borderBottom: '1px solid rgba(224, 224, 224, .5)',
                padding: '14px 40px 14px 24px',
            },
            head: {
                fontSize: '0.72rem',
                padding: '0.5em',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#8A8A80',
            },
            body: {
                fontSize: '0.8rem',
                padding: '0.5em'
            },
            paddingCheckbox: {
                padding: '0 0 0 0px',
            },
        }
    },
}

const darkModeOverrides = {
    overrides: {
        MuiCssBaseline: {
            '@global': {
                // '*': {
                //     'scrollbar-width': 'thin',
                // },
                '*::-webkit-scrollbar': {
                    width: '0.4em',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#12121A',
                },
            }
        },
        MuiPaper: {
            root: {
                backgroundColor: '#23232D',
                boxShadow:
                    '0px 1px 8px rgba(0, 0, 0, 0.103475), 0px 3px 3px rgba(0, 0, 0, 0.0988309), 0px 3px 4px rgba(0, 0, 0, 0.10301) !important',
                '&::-webkit-scrollbar': {
                    width: '0.4em',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#12121A',
                },
            },
        },
        MuiAppBar: {
            root: {
                backgroundColor: '#23232D !important',
            },
        },
        MuiButton: {
            root: {
                boxShadow: 'none !important',
            },
        },
        typography: {
            root: {
                color: 'white !important',
            },
        },
        MuiCheckbox: {
            root: {
                color: '#76767B',
            },
        },
        MuiTable: {
            root: {
                '&::-webkit-scrollbar': {
                    width: '0.4em',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#12121A',
                },
            },
        },
        MuiTableCell: {
            head: {
                color: '#76767B',
            },
        },
      MuiTableBody: {
        root: {
          '&::-webkit-scrollbar': {
            width: '0.4em',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#12121A',
          },
        }
      },
        MuiTableSortLabel: {
            icon: {
                color: '#76767B',
            },
            active: {
                color: '#76767B !important',
            },
            iconDirectionDesc: {
                color: '#76767B !important',
            },
            iconDirectionAsc: {
                color: '#76767B !important',
            },
        },
        MuiTablePagination: {
            toolbar: {
                color: '#76767B',
            },
            selectIcon: {
                color: '#76767B',
            },
        },
    },
}

export default {
    default: createMuiTheme({ ...defaultTheme, ...overrides }),
    secondary: createMuiTheme({ ...secondaryTheme, ...overrides }),
    success: createMuiTheme({ ...successTheme, ...overrides }),
    dark: createMuiTheme({ ...darkTheme, ...overrides, ...darkModeOverrides }),
}
